import { isNull } from 'helpers/nullCheck';

export type PermissionInput = string | string[] | null | undefined;
type PermissionType = 'capability' | 'flow' | 'role';

export class PermissionService {
	private static _getCapabilities(): string[] {
		const capabilitiesString = localStorage.getItem('roles');

		try {
			if (isNull(capabilitiesString)) return [];

			const capabilities = JSON.parse(capabilitiesString!);

			if (isNull(capabilities) || typeof capabilities !== 'object') return [];

			return Object.keys(capabilities);
		} catch (error) {
			return [];
		}
	}

	private static _getRoles(): string[] {
		const rolesString = localStorage.getItem('roleIds');

		try {
			if (isNull(rolesString)) return [];

			const roles = JSON.parse(rolesString!);

			if (!Array.isArray(roles)) return [];

			return roles;
		} catch (error) {
			return [];
		}
	}

	private static _getFlows(): string[] {
		const flowsString = localStorage.getItem('assignmentFlows');

		try {
			if (isNull(flowsString)) return [];

			const flows = JSON.parse(flowsString!);

			if (!Array.isArray(flows)) return [];

			return flows;
		} catch (error) {
			return [];
		}
	}

	private static _getPermissionType(permission: string): PermissionType {
		const isFlowPermission = permission.startsWith('flow:');
		if (isFlowPermission) {
			return 'flow';
		}

		const isRolePermission = permission.startsWith('role:');
		if (isRolePermission) {
			return 'role';
		}

		return 'capability';
	}

	private static _hasFlow(flow: string, flows: string[]): boolean {
		// Remove "flow:"
		const flowId = flow.substring(5);
		return flows.includes(flowId);
	}

	private static _hasRole(role: string, roles: string[]): boolean {
		// Remove "role:"
		const roleName = role.substring(5);
		return roles.includes(roleName);
	}

	private static _hasCapability(capability: string, capabilities: string[]): boolean {
		return capabilities.includes(capability);
	}

	public static hasPermission(permission: PermissionInput): boolean {
		if (isNull(permission)) return true;

		const permissionsToCheck = Array.isArray(permission) ? permission : [permission];

		if (permissionsToCheck.length == 0) {
			return true;
		}

		const flows = this._getFlows();
		const roles = this._getRoles();
		const capabilities = this._getCapabilities();

		return permissionsToCheck.every((permissionName) => {
			const permissionType = this._getPermissionType(permissionName!);

			switch (permissionType) {
				case 'flow': {
					return this._hasFlow(permissionName!, flows);
				}
				case 'role': {
					return this._hasRole(permissionName!, roles);
				}
				default: {
					return this._hasCapability(permissionName!, capabilities);
				}
			}
		});
	}
}
